import { Button } from "@/components/ui/button";
import { CircleAlert, type LucideIcon, X } from "lucide-react";
import { Dialog } from "radix-ui";
import type { ReactNode, RefObject } from "react";

export function SettingsSection({
  title,
  description,
  icon: Icon,
  children,
  action,
  badge,
}: {
  title: string;
  description: string;
  icon: LucideIcon;
  children: ReactNode;
  action?: ReactNode;
  badge?: string;
}) {
  return (
    <section className="settings-section">
      <header className="settings-section-heading">
        <span className="settings-section-icon">
          <Icon size={21} strokeWidth={1.6} aria-hidden="true" />
        </span>
        <div>
          <div className="settings-title-line">
            <h2>{title}</h2>
            {badge && <span className="settings-badge">{badge}</span>}
          </div>
          <p>{description}</p>
        </div>
        {action && <div className="settings-heading-action">{action}</div>}
      </header>
      <div className="settings-stack">{children}</div>
    </section>
  );
}

export function SettingsCard({
  title,
  description,
  children,
  className = "",
}: { title: string; description?: string; children: ReactNode; className?: string }) {
  return (
    <div className={`settings-card ${className}`}>
      <div className="settings-card-heading">
        <h3>{title}</h3>
        {description && <p>{description}</p>}
      </div>
      {children}
    </div>
  );
}

export function SettingsError({ error, retry }: { error: Error; retry: () => void }) {
  return (
    <div className="settings-error" role="alert">
      <CircleAlert size={19} aria-hidden="true" />
      <div>
        <strong>Couldn't load these settings</strong>
        <p>{error.message || "Please try again."}</p>
        <Button variant="outline" size="sm" onClick={retry}>
          Retry
        </Button>
      </div>
    </div>
  );
}

export function SettingsDialog({
  title,
  description,
  children,
  onClose,
  busy = false,
  deliberateClose = false,
  returnFocus,
}: {
  title: string;
  description: string;
  children: ReactNode;
  onClose: () => void;
  busy?: boolean;
  deliberateClose?: boolean;
  returnFocus?: RefObject<HTMLButtonElement | null>;
}) {
  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="settings-dialog-overlay" />
        <Dialog.Content
          className="settings-dialog"
          onEscapeKeyDown={(event) => {
            if (busy || deliberateClose) event.preventDefault();
          }}
          onInteractOutside={(event) => {
            if (busy || deliberateClose) event.preventDefault();
          }}
          onCloseAutoFocus={(event) => {
            if (returnFocus) {
              event.preventDefault();
              returnFocus.current?.focus();
            }
          }}
        >
          <Dialog.Title>{title}</Dialog.Title>
          <Dialog.Description>{description}</Dialog.Description>
          {children}
          {!deliberateClose && (
            <Dialog.Close className="settings-dialog-close" disabled={busy} aria-label="Close">
              <X size={18} />
            </Dialog.Close>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
